import { Vars } from '@shared/game-variables';
import { NullableDateTimeSchema } from '@shared/modelTypes/util-types';
import { z } from 'zod';

export const teamNames = ['alpha', 'beta', 'delta', 'gamma'] as const;
export const TeamNameSchema = z.enum(teamNames);
export type TeamName = z.infer<typeof TeamNameSchema>;

export const teamNameOptions: { value: TeamName; label: string }[] = [
	{ value: 'alpha', label: 'Alpha' },
	{ value: 'beta', label: 'Beta' },
	{ value: 'delta', label: 'Delta' },
	{ value: 'gamma', label: 'Gamma' },
];

export const teamRoles = ['operator', 'tech-team-one', 'tech-team-two'] as const;
export const TeamRoleSchema = z.enum(teamRoles);
export type TeamRole = z.infer<typeof TeamRoleSchema>;

export const teamRoleOptions: { value: TeamRole; label: string }[] = [
	{ value: 'operator', label: 'Operator' },
	{ value: 'tech-team-one', label: 'Tech Team 1' },
	{ value: 'tech-team-two', label: 'Tech Team 2' },
];

export const teamRoleNames = {
	operator: 'Operator',
	'tech-team-one': 'Tech Team 1',
	'tech-team-two': 'Tech Team 2',
};

export const roomStatus = ['closed', 'startup', 'repairing', 'success', 'failed'] as const;
export const RoomStatusSchema = z.enum(roomStatus);
/**
 * - `closed`:  start state of the room, players can't do anything with the room
 * - `startup`: tech teams can be assigned to the room and time is running, a tech team need to reach the room in time and start repairing
 * - `repairing`: a tech team reached the room in time and is trying to fix the problems in time
 * - `success`: a tech team fixed the problems in time and the room is fixed successfully
 * - `failed`: a tech team didn't reach the room in time or didn't fix the problems in time. The room isn't successfully fixed
 */
export type RoomStatus = z.infer<typeof RoomStatusSchema>;

export const roomStatusOptions: { value: RoomStatus; label: string }[] = [
	{ value: 'closed', label: 'Closed' },
	{ value: 'startup', label: 'Startup' },
	{ value: 'repairing', label: 'Repairing' },
	{ value: 'success', label: 'Success' },
	{ value: 'failed', label: 'Failed' },
];

export const sessionStatus = ['closed', 'open', 'running'] as const;
export const SessionStatusSchema = z.enum(sessionStatus);

/**
 * - `closed`:  this session has ended, players may see results but new players can no longer join
 * - `open`:    session has been opened but the game has not started yet. the players can join but can't do anything else yet
 * - `running`: the game is running, you can play the game
 */
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

export const sessionStatusOptions: { value: SessionStatus; label: string }[] = [
	{ value: 'closed', label: 'Closed' },
	{ value: 'open', label: 'Open' },
	{ value: 'running', label: 'Running' },
];

export const DeviceIdSchema = z.uuid();
export type DeviceId = z.infer<typeof DeviceIdSchema>;

/**
 * Composite of teamName + role, e.g. "alpha-operator"
 */
export const MemberIdSchema = z.templateLiteral([TeamNameSchema, '-', TeamRoleSchema]);
export type MemberId = z.infer<typeof MemberIdSchema>;

export const MemberSchema = z.object({
	deviceId: DeviceIdSchema.nullable(),
	team: TeamNameSchema,
	role: TeamRoleSchema,
	connectionTime: NullableDateTimeSchema,
	shiftStarted: z.boolean().default(false),
});
export type Member = z.infer<typeof MemberSchema>;

export const RoomStateSchema = z.object({
	id: z.uuid(),
	status: RoomStatusSchema.default('closed'),
	startupTime: NullableDateTimeSchema,
	startRepairingTime: NullableDateTimeSchema,
	endRepairingTime: NullableDateTimeSchema,
	assignedMemberId: MemberIdSchema.nullable().default(null),
	extendedTimeCount: z.int().default(0),
});
export type RoomState = z.infer<typeof RoomStateSchema>;

export const SessionIdSchema = z.uuid();
export type SessionId = z.infer<typeof SessionIdSchema>;

export const ChatMessageSchema = z.object({
	memberId: MemberIdSchema,
	message: z.string(),
	createdAt: z.date(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatsSchema = z.record(MemberIdSchema, z.array(ChatMessageSchema));
export type Chats = z.infer<typeof ChatsSchema>;

export const TeamStateSchema = z.object({
	score: z.number().default(0),
});
export type TeamState = z.infer<typeof TeamStateSchema>;

const TeamStateRecordSchema = z.record(TeamNameSchema, TeamStateSchema);
export type TeamStateRecord = z.infer<typeof TeamStateRecordSchema>;

export const defaultTeamStateRecord: Record<TeamName, { score: number }> = {
	alpha: { score: 0 },
	beta: { score: 0 },
	delta: { score: 0 },
	gamma: { score: 0 },
};

export const SessionSchema = z.object({
	id: SessionIdSchema,
	locationId: z.uuid(),
	status: SessionStatusSchema.default('closed'),
	startTime: NullableDateTimeSchema,
	gameOverTime: NullableDateTimeSchema,
	systemDamage: z.number().min(Vars.minSystemDamage).max(Vars.maxSystemDamage).default(Vars.defaultSystemDamage),
	members: z.array(MemberSchema).default([]),
	roomStates: z.array(RoomStateSchema).default([]),
	teamStates: TeamStateRecordSchema,
	chats: ChatsSchema,
});

export const SetPlayerSchema = z.object({
	team: TeamNameSchema,
	role: TeamRoleSchema,
	deviceId: DeviceIdSchema,
	sessionId: SessionIdSchema,
});
export type SetPlayer = z.infer<typeof SetPlayerSchema>;

export const RoomClientSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	repairTime: z.number().min(10).default(600),
	startupTime: z.number().min(5).default(60),
	status: z.enum(roomStatus).default('closed'),
	startTime: NullableDateTimeSchema,
	endTime: NullableDateTimeSchema,
	memberId: MemberSchema.shape.deviceId.nullable().default(null),
});
export type RoomClient = z.infer<typeof RoomClientSchema>;
export type Session = z.infer<typeof SessionSchema>;
